import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import DeleteIcon from "../../assets/icons/Trash.svg";
import { useAuth } from "../../contexts/AuthContext";
import { useDashboard } from "../../contexts/DashboardProvider";
import useCrudFactory from "../../hooks/useCrudFactory";
import apiService from "../../services/apiService";
import CloseDialogButton from "../CloseDialogButton";
import ImageUpload from "../ImageUpload";

const initialState = (languages) => {
  return {
    title: "",
    image: "",
    menu_id: null,
    category_id: null,
    item_id: null,
    languages: languages.map((lang) => ({
      language_id: lang.language_id,
      title: "",
    })),
    active: true,
  };
};

const PromotionalPopupForm = ({ open, onClose, onSave, popupId = null }) => {
  const { languages } = useAuth();
  const {
    reloadData,
    itemsCrud: { data: allItems },
    menusCrud: { data: menus },
  } = useDashboard();

  const promotionalPopupsCrud = useCrudFactory("/promotional-popups");

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({ ...initialState(languages) });
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(false);

  const [originalTitle, setOriginalTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTogglingActive, setIsTogglingActive] = useState(false);

  useEffect(() => {
    const loadPopup = async () => {
      if (popupId) {
        try {
          const popup = await promotionalPopupsCrud.getDataById(popupId);
          setOriginalTitle(popup.title);
          const popupLanguages = popup.languages || [];
          const populatedLanguages = languages.map((lang) => {
            const existingLanguage = popupLanguages?.find(
              (popupLang) =>
                popupLang.language_id.toString() ===
                lang.language_id.toString()
            );
            return {
              language_id: lang.language_id,
              title: existingLanguage ? existingLanguage.title : "",
            };
          });

          setFormData({
            title: popup.title,
            image: popup.image || "",
            menu_id: popup.menu_id,
            category_id: popup.category_id,
            item_id: popup.item_id,
            languages: populatedLanguages,
            active: !!popup.active,
          });

          // Fetch categories for the menu if menu_id exists
          if (popup.menu_id) {
            try {
              const { data: { data: items } } = await apiService.get(`/items/menu/${popup.menu_id}`);
              const activeItems = items.filter(item => item.active !== false && item.active !== 0);

              const categoriesMap = new Map();
              activeItems.forEach(item => {
                if (item.categories && Array.isArray(item.categories)) {
                  item.categories.forEach(category => {
                    if (category.id && !categoriesMap.has(category.id)) {
                      categoriesMap.set(category.id, {
                        id: category.id,
                        name: category.name,
                      });
                    }
                  });
                }
              });
              const uniqueCategories = Array.from(categoriesMap.values());
              setFilteredCategories(uniqueCategories);

              if (popup.category_id) {
                const categoryItems = activeItems.filter(item =>
                  item.categories && item.categories.some(cat => Number(cat.id) === Number(popup.category_id))
                );
                setFilteredItems(categoryItems);
              } else {
                setFilteredItems(activeItems);
              }
            } catch (error) {
              console.error("Error fetching items for menu:", error);
            }
          }
        } catch (error) {
          console.error("Error fetching promotional popup:", error);
        }
      } else {
        resetFormData();
      }
    };
    if (open) loadPopup();
    else {
      setFormData({ ...initialState(languages) });
      setOriginalTitle("");
      setFilteredItems([]);
    }
  }, [popupId, open]);

  // Fetch categories when menu changes
  useEffect(() => {
    const fetchCategoriesByMenu = async () => {
      if (formData.menu_id) {
        setIsLoadingCategories(true);
        try {
          const { data: { data: items } } = await apiService.get(`/items/menu/${formData.menu_id}`);
          const activeItems = items.filter(item => item.active !== false && item.active !== 0);

          const categoriesMap = new Map();
          activeItems.forEach(item => {
            if (item.categories && Array.isArray(item.categories)) {
              item.categories.forEach(category => {
                if (category.id && !categoriesMap.has(category.id)) {
                  categoriesMap.set(category.id, {
                    id: category.id,
                    name: category.name,
                  });
                }
              });
            }
          });
          const uniqueCategories = Array.from(categoriesMap.values());
          setFilteredCategories(uniqueCategories);
          setFilteredItems(activeItems);
        } catch (error) {
          console.error("Error fetching categories for menu:", error);
          setFilteredCategories([]);
          setFilteredItems([]);
        } finally {
          setIsLoadingCategories(false);
        }
      } else {
        setFilteredCategories([]);
        setFilteredItems([]);
      }
    };

    fetchCategoriesByMenu();
  }, [formData.menu_id]);

  // Filter items when category changes
  useEffect(() => {
    const filterItemsByCategory = async () => {
      if (formData.category_id && formData.menu_id) {
        setIsLoadingItems(true);
        try {
          const { data: { data: items } } = await apiService.get(`/items/menu/${formData.menu_id}`);
          const activeItems = items.filter(item => item.active !== false && item.active !== 0);
          const categoryItems = activeItems.filter(item =>
            item.categories && item.categories.some(cat => Number(cat.id) === Number(formData.category_id))
          );
          setFilteredItems(categoryItems);
        } catch (error) {
          console.error("Error filtering items by category:", error);
          setFilteredItems([]);
        } finally {
          setIsLoadingItems(false);
        }
      }
    };

    filterItemsByCategory();
  }, [formData.category_id]);

  const resetFormData = () => {
    setFormData({ ...initialState(languages) });
    setOriginalTitle("");
    setFilteredItems([]);
  };

  const handleTitleChange = (e) => {
    setFormData((prevData) => ({ ...prevData, title: e.target.value }));
  };

  const handleImageUpload = (imageUrl) => {
    setFormData((prevData) => ({ ...prevData, image: imageUrl }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleLanguageChange = (languageId, field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      languages: prevData.languages.map((lang) =>
        lang.language_id === languageId ? { ...lang, [field]: value } : lang
      ),
    }));
  };

  const handleMenuChange = (event, value) => {
    setFormData((prevData) => ({
      ...prevData,
      menu_id: value ? value.id : null,
      category_id: null, // Reset category when menu changes
      item_id: null, // Reset item when menu changes
    }));
  };

  const handleCategoryChange = (event, value) => {
    setFormData((prevData) => ({
      ...prevData,
      category_id: value ? value.id : null,
      item_id: null, // Reset item when category changes
    }));
  };

  const handleItemChange = (event, value) => {
    setFormData((prevData) => ({
      ...prevData,
      item_id: value ? value.id : null,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    const updatedData = {
      title: formData.title,
      content: null,
      image: formData.image,
      menu_id: formData.menu_id,
      category_id: formData.category_id,
      item_id: formData.item_id,
      languages: formData.languages.map(lang => ({
        language_id: lang.language_id,
        title: lang.title,
        content: null,
      })),
    };
    try {
      const responseData = popupId
        ? await promotionalPopupsCrud.updateData(popupId, updatedData)
        : await promotionalPopupsCrud.createData(updatedData);
      onSave(updatedData);
      onClose();
      reloadData();
    } catch (err) {
      console.error("Error saving promotional popup:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this promotional popup?")) {
      setIsDeleting(true);
      try {
        await promotionalPopupsCrud.deleteData(popupId);
        onClose();
        onSave(popupId);
        reloadData();
      } catch (err) {
        console.error("Error deleting promotional popup:", err);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleToggleActiveState = async (event) => {
    setIsTogglingActive(true);
    const newActiveState = event.target.checked;
    try {
      await promotionalPopupsCrud.setActivationState(popupId, newActiveState);
      setFormData((prevData) => ({ ...prevData, active: newActiveState }));
      reloadData();
    } catch (err) {
      console.error("Error updating active state:", err);
    } finally {
      setIsTogglingActive(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!promotionalPopupsCrud.loading ? onClose : undefined}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={promotionalPopupsCrud.loading}
    >
      <DialogTitle>
        {popupId ? `Edit Popup` : "Add Popup"}
        <CloseDialogButton onClose={onClose} loading={promotionalPopupsCrud.loading} />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {languages.findIndex((lang) => lang.language_name === "English") ===
              -1 && <Tab label="English" sx={{ flex: 1 }} />}
            {languages.map((lang) => (
              <Tab
                key={lang.language_id}
                label={lang.language_name}
                sx={{ flex: 1 }}
              />
            ))}
          </Tabs>
        </Box>

        <Box marginTop={2}>
          {activeTab === 0 ? (
            <>
              <ImageUpload
                key={formData.image}
                imageUrl={formData.image}
                onImageUpload={handleImageUpload}
                disabled={promotionalPopupsCrud.loading}
              />
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={handleTitleChange}
                margin="normal"
                required
                error={!!promotionalPopupsCrud.fieldErrors.title}
                helperText={promotionalPopupsCrud.fieldErrors.title}
                disabled={promotionalPopupsCrud.loading}
              />
              <Autocomplete
                options={menus}
                getOptionLabel={(menu) => menu.name || "Unnamed Menu"}
                value={menus.find((menu) => Number(menu.id) === Number(formData.menu_id)) || null}
                onChange={handleMenuChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Menu (Optional)"
                    placeholder="Choose a menu"
                    error={!!promotionalPopupsCrud.fieldErrors.menu_id}
                    helperText={promotionalPopupsCrud.fieldErrors.menu_id}
                    margin="normal"
                  />
                )}
                disabled={promotionalPopupsCrud.loading}
              />
              <Autocomplete
                options={filteredCategories}
                getOptionLabel={(category) => category.name || "Unnamed Category"}
                value={filteredCategories.find((category) => Number(category.id) === Number(formData.category_id)) || null}
                onChange={handleCategoryChange}
                loading={isLoadingCategories}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Category (Optional)"
                    placeholder={isLoadingCategories ? "Loading categories..." : "Choose a category"}
                    error={!!promotionalPopupsCrud.fieldErrors.category_id}
                    helperText={promotionalPopupsCrud.fieldErrors.category_id}
                    margin="normal"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingCategories ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                disabled={promotionalPopupsCrud.loading || !formData.menu_id || isLoadingCategories}
              />
              <Autocomplete
                options={filteredItems}
                getOptionLabel={(item) => item.name || "Unnamed Item"}
                value={filteredItems.find((item) => Number(item.id) === Number(formData.item_id)) || null}
                onChange={handleItemChange}
                loading={isLoadingItems}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Item (Optional)"
                    placeholder={isLoadingItems ? "Loading items..." : "Choose an item"}
                    error={!!promotionalPopupsCrud.fieldErrors.item_id}
                    helperText={promotionalPopupsCrud.fieldErrors.item_id}
                    margin="normal"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingItems ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                disabled={promotionalPopupsCrud.loading || !formData.category_id || isLoadingItems}
              />
            </>
          ) : (
            <>
              <TextField
                fullWidth
                label={`${languages[activeTab]?.language_name} title`}
                value={formData.languages[activeTab]?.title || ""}
                onChange={(e) =>
                  handleLanguageChange(
                    languages[activeTab].language_id,
                    "title",
                    e.target.value
                  )
                }
                margin="normal"
                error={!!promotionalPopupsCrud.fieldErrors[`languages.${activeTab - 1}.title`]}
                helperText={promotionalPopupsCrud.fieldErrors[`languages.${activeTab - 1}.title`]}
                disabled={promotionalPopupsCrud.loading}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: "column",
          alignItems: "flex-end",
          px: 3,
          mb: 2,
        }}
      >
        {popupId && (
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={handleToggleActiveState}
                color="primary"
                disabled={promotionalPopupsCrud.loading}
              />
            }
            label="Active Status"
          />
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            ...(popupId && { flexDirection: "row-reverse" }),
          }}
        >
          {popupId && (
            <IconButton
              onClick={handleDelete}
              sx={{
                color: "red",
                border: "1px solid #F4F4F5",
                borderRadius: "6px",
              }}
              disabled={promotionalPopupsCrud.loading || isDeleting}
            >
              {isDeleting ? (
                <CircularProgress size={16} />
              ) : (
                <img src={DeleteIcon} alt="My Icon" width={20} height={20} />
              )}
            </IconButton>
          )}
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={promotionalPopupsCrud.loading || isSaving}
            sx={{ px: 4 }}
          >
            {isSaving ? (
              <CircularProgress size={24} />
            ) : popupId ? (
              "Update"
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PromotionalPopupForm;
